/**
 * SectionJourneyCards — three small claymation journey cards placed
 * beneath each home-page section. Static (no fade rotation), with
 * rounded corners and on-brand border + shadow.
 *
 * Layout:
 *   Desktop / tablet ≥ md: three cards in a row, optional arrow icons
 *     between them when `withArrows` is true (Section 1 only).
 *   Mobile (< md): single-card swipe carousel using CSS scroll-snap.
 *
 * Props:
 *   sectionId  — one of `hero | how_it_works | what_happens | why_choose | cta`
 *   withArrows — render small chevron icons between desktop cards
 */
import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import useMobileAutoScroll from "@/lib/useMobileAutoScroll";

export default function SectionJourneyCards({ sectionId, withArrows = false }) {
    const [frames, setFrames] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const mobileTrackRef = useRef(null);
    useMobileAutoScroll(mobileTrackRef, {
        itemCount: frames.length,
        intervalMs: 4000,
    });

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get(`/home-journey/${sectionId}`);
                if (!alive) return;
                setFrames(r.data?.frames || []);
            } catch {
                // Pre-generation phase — render nothing, gracefully.
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [sectionId]);

    if (!loaded || frames.length === 0) return null;

    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const resolve = (u) => (u && u.startsWith("/") ? `${base}${u}` : u);

    return (
        <div
            className="mt-10 md:mt-14"
            data-testid={`section-cards-${sectionId}`}
        >
            {/* Desktop row — 3 cards side by side with optional arrows */}
            <div className="hidden md:flex items-center gap-4 lg:gap-6 justify-center">
                {frames.map((f, i) => (
                    <CardFragment
                        key={`d-${f.frame_index}`}
                        sectionId={sectionId}
                        frame={f}
                        index={i}
                        resolve={resolve}
                    >
                        {withArrows && i < frames.length - 1 ? (
                            <Arrow />
                        ) : null}
                    </CardFragment>
                ))}
            </div>

            {/* Mobile carousel — CSS scroll-snap, one card per "page".
                Auto-advances every 4 s through ALL frames then loops
                forward via a cloned first card (instant-jump after the
                smooth-scroll settles) so the wrap-around feels like
                continuation, not a reverse rewind. Pauses on touch /
                wheel for 6 s. */}
            <div
                ref={mobileTrackRef}
                className="md:hidden flex gap-4 overflow-x-auto px-1 pb-3 snap-x snap-mandatory scroll-smooth"
                style={{ scrollbarWidth: "none" }}
                data-testid={`section-cards-${sectionId}-mobile`}
            >
                {frames.map((f) => (
                    <div
                        key={`m-${f.frame_index}`}
                        className="snap-center shrink-0 w-[82vw] max-w-[360px]"
                    >
                        <CardImage
                            sectionId={sectionId}
                            frame={f}
                            resolve={resolve}
                        />
                    </div>
                ))}
                {/* Clone of the first frame for seamless forward loop —
                    `aria-hidden` + `tabIndex={-1}` keeps it out of the
                    a11y tree and tab order; the auto-scroll hook
                    instant-jumps back to the real first card the moment
                    this clone settles into view. */}
                {frames.length > 1 ? (
                    <div
                        key="m-clone-first"
                        className="snap-center shrink-0 w-[82vw] max-w-[360px]"
                        aria-hidden="true"
                        tabIndex={-1}
                        data-testid={`section-cards-${sectionId}-mobile-clone`}
                    >
                        <CardImage
                            sectionId={sectionId}
                            frame={frames[0]}
                            resolve={resolve}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Desktop fragment — card + optional trailing arrow (rendered as a
 * sibling so arrows live between cards, not inside them). Keeps the
 * 3-card row vertically aligned.
 */
function CardFragment({ sectionId, frame, index, resolve, children }) {
    return (
        <>
            <div className="flex-1 max-w-[360px]">
                <CardImage
                    sectionId={sectionId}
                    frame={frame}
                    resolve={resolve}
                />
            </div>
            {children}
        </>
    );
}

function CardImage({ sectionId, frame, resolve }) {
    return (
        <div
            className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-bl bg-bl-surface shadow-[0_20px_40px_-24px_rgba(31,36,40,0.22)]"
            data-testid={`section-card-${sectionId}-${frame.frame_index}`}
        >
            <img
                src={resolve(frame.public_url)}
                alt=""
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
            />
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    boxShadow:
                        "inset 0 0 40px 0 rgba(31,36,40,0.10), "
                        + "inset 0 0 0 1px rgba(31,36,40,0.03)",
                }}
            />
        </div>
    );
}

function Arrow() {
    return (
        <div
            className="shrink-0 text-bl-primary/60"
            aria-hidden="true"
            data-testid="section-cards-arrow"
        >
            <ChevronRight className="w-6 h-6 lg:w-7 lg:h-7" strokeWidth={1.5} />
        </div>
    );
}
